import React, { useEffect, useRef } from 'react';

/**
 * Terminal animation — exact port of the inline <script> in index.html.
 * Class names match the static prototype exactly:
 *   .terminal-wrapper, .terminal, .terminal-bar, .dots, .dot, .terminal-body
 *   .t-line, .t-blank, .t-prompt, .t-cmd, .t-section, .t-check, .t-label,
 *   .t-value, .t-accent, .t-success, .t-output, .t-output-val, .cursor
 */

interface Line {
    type: string;
    text?: string;
    label?: string;
    value?: string;
    accent?: string;
}

const LINES: Line[] = [
    { type: 'prompt', text: 'run ai-value-discovery --client=enterprise' },
    { type: 'blank' },
    { type: 'section', text: '> Initializing PERCEPTYA AI VALUE SYSTEM™...' },
    { type: 'blank' },
    { type: 'section', text: '> Phase 1: AI Value Discovery' },
    { type: 'check', label: 'Executive alignment', value: 'complete' },
    { type: 'check', label: 'AI Maturity Score', value: '2.4 / 5.0', accent: '→ gap analysis ready' },
    { type: 'check', label: 'Use cases identified', value: '23' },
    { type: 'check', label: 'Top 7 prioritized', value: 'by economic impact' },
    { type: 'check', label: 'Financial Impact Model', value: '$4.2M', accent: '(12 months)' },
    { type: 'check', label: 'Architecture Blueprint', value: 'Data Foundation + Agent Layer' },
    { type: 'check', label: '18-month Roadmap', value: 'sequenced · costed · board-ready' },
    { type: 'blank' },
    { type: 'success', text: 'SUCCESS: AI Value Discovery complete.' },
    { type: 'output', label: 'Output →', value: 'Roadmap_v1.pdf | Impact_Model.xlsx' },
    { type: 'blank' },
    { type: 'cursor', text: '> Ready to industrialize. Run Phase 2? [Y/n]: ' },
];

function buildLine(d: Line): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 't-line';
    if (d.type === 'blank') { el.className = 't-line t-blank'; return el; }
    if (d.type === 'prompt')
        el.innerHTML = `<span class="t-prompt">user@perceptya:~$</span><span class="t-cmd"> ${d.text}</span>`;
    else if (d.type === 'section')
        el.innerHTML = `<span class="t-section">${d.text}</span>`;
    else if (d.type === 'check') {
        let h = `<span class="t-check">  [✓]</span><span class="t-label"> ${d.label}: </span><span class="t-value">${d.value}</span>`;
        if (d.accent) h += `<span class="t-accent"> ${d.accent}</span>`;
        el.innerHTML = h;
    }
    else if (d.type === 'success')
        el.innerHTML = `<span class="t-success">  ${d.text}</span>`;
    else if (d.type === 'output')
        el.innerHTML = `<span class="t-output">  ${d.label} </span><span class="t-output-val">${d.value}</span>`;
    else if (d.type === 'cursor')
        el.innerHTML = `<span class="t-section">${d.text}</span><span class="cursor"></span>`;
    return el;
}

const TerminalAnimation: React.FC = () => {
    const bodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const body = bodyRef.current;
        if (!body) return;

        let cur = 0;
        let timerId: ReturnType<typeof setTimeout>;
        let destroyed = false;

        function next() {
            if (destroyed || !body) return;
            if (cur >= LINES.length) { timerId = setTimeout(restart, 5000); return; }
            const d = LINES[cur++];
            const el = buildLine(d);
            body.appendChild(el);
            requestAnimationFrame(() => el.classList.add('visible'));
            const delay = d.type === 'blank' ? 55
                : d.type === 'prompt' ? 420
                : d.type === 'success' ? 300
                : d.type === 'cursor' ? 0
                : 120;
            timerId = setTimeout(next, delay);
        }

        function restart() {
            if (destroyed || !body) return;
            body.querySelectorAll('.t-line').forEach(l => {
                (l as HTMLElement).style.transition = 'opacity .3s';
                (l as HTMLElement).style.opacity = '0';
            });
            setTimeout(() => {
                if (destroyed || !body) return;
                body.innerHTML = '';
                cur = 0;
                timerId = setTimeout(next, 400);
            }, 480);
        }

        timerId = setTimeout(next, 900);

        return () => { destroyed = true; clearTimeout(timerId); };
    }, []);

    return (
        <div className="terminal-wrapper">
            <div className="terminal">
                <div className="terminal-bar">
                    <div className="dots">
                        <div className="dot dot-r" />
                        <div className="dot dot-y" />
                        <div className="dot dot-g" />
                    </div>
                    <span className="terminal-title">perceptya — ai-value-discovery</span>
                </div>
                <div className="terminal-body" ref={bodyRef} />
            </div>
        </div>
    );
};

export default TerminalAnimation;
